import React, { useState, useEffect } from "react";

// Custom Components
import LoginHeader from "./components/LoginHeader.js";
import SideNavigation from "./components/SideNavigation.js";
import AADHeader from "./components/AADHeader.js";

// Material-UI Components
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import Button from "@mui/material/Button";
import WestIcon from "@mui/icons-material/West";
import { Grid, Typography, Box, CircularProgress, Backdrop } from "@mui/material";

// Third-party Components
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Checkbox } from "primereact/checkbox";
import { useNavigate, useLocation } from "react-router-dom";
import { BlobServiceClient } from "@azure/storage-blob";

const steps = ["Start", "Materiality", "Module", "Input", "Report"];

// inside ReportDashboard.js, above your component:
const DR_RANGES = [
  { id: "general-disclosures", label: "ESRS 2 General Disclosures", start: 1, end: 125, actualCount: 109 },
  { id: "climate-change", label: "ESRS E1 Climate change", start: 126, end: 261, actualCount: 135 },
  { id: "pollution", label: "ESRS E2 Pollution", start: 262, end: 336, actualCount: 73 },
  { id: "water-marine-resources", label: "ESRS E3 Water and marine resources", start: 337, end: 407, actualCount: 69 },
  { id: "biodiversity-ecosystems", label: "ESRS E4 Biodiversity & ecosystems", start: 408, end: 549, actualCount: 132 },
  { id: "resource-circular-economy", label: "ESRS E5 Resource use & circular economy", start: 550, end: 638, actualCount: 80 },

  { id: "own-workforce", label: "ESRS S1 Own workforce", start: 639, end: 813, actualCount: 155 },
  { id: "workers-value-chain", label: "ESRS S2 Workers in the value chain", start: 814, end: 909, actualCount: 83 },
  { id: "affected-communities", label: "ESRS S3 Affected communities", start: 910, end: 1005, actualCount: 96 },
  { id: "consumers-end-users", label: "ESRS S4 Consumers and end users", start: 1006, end: 1100, actualCount: 86 },

  { id: "business-conduct", label: "ESRS G1 Business Conduct", start: 1101, end: 1163, actualCount: 49 },
];

const DR_SUBTOPICS = [
  // GENERAL
  { label: "BP-1 – General basis for preparation of the sustainability statement", start: 1, end: 6, actualCount: 5, pillar: "General" },
  { label: "BP-2 – Disclosures in relation to specific circumstances", start: 7, end: 33, actualCount: 21, pillar: "General" },
  { label: "GOV-1 – The role of the administrative, management and supervisory bodies", start: 34, end: 49, actualCount: 10, pillar: "General" },
  { label: "GOV-2 – Information provided to and sustainability matters addressed by the undertaking's administrative, management and supervisory bodies", start: 50, end: 53, actualCount: 4, pillar: "General" },
  { label: "GOV-3 – Integration of sustainability-related performance in incentive schemes", start: 54, end: 59, actualCount: 5, pillar: "General" },
  { label: "GOV–4 - Statement on due diligence", start: 60, end: 60, actualCount: 1, pillar: "General" },
  { label: "GOV–5 - Risk management and internal controls over sustainability reporting", start: 61, end: 65, actualCount: 5, pillar: "General" },
  { label: "SBM-1 – Strategy, business model and value chain", start: 66, end: 84, actualCount: 17, pillar: "General" },
  { label: "SBM-2 – Interests and views of stakeholders", start: 85, end: 96, actualCount: 12, pillar: "General" },
  { label: "SBM-3 - Material impacts, risks and opportunities and their interaction with strategy and business model", start: 97, end: 106, actualCount: 10, pillar: "General" },
  { label: "IRO-1 - Description of the process to identify and assess material impacts, risks and opportunities", start: 107, end: 121, actualCount: 15, pillar: "General" },
  { label: "IRO-2 – Disclosure Requirements in ESRS covered by the undertaking's sustainability statement", start: 122, end: 125, actualCount: 4, pillar: "General" },

  // CLIMATE
  { label: "ESRS 2 GOV-3 Integration of sustainability related performance in incentive schemes", start: 126, end: 126, actualCount: 1, pillar: "Environment" },
  { label: "E1-1 – Transition plan for climate change mitigation", start: 127, end: 138, actualCount: 12, pillar: "Environment" },
  { label: "E1 ESRS 2 SBM-3 – Material impacts, risks and opportunities and their interaction with strategy and business model", start: 139, end: 143, actualCount: 5, pillar: "Environment" },
  { label: "ESRS 2 IRO-1 – Description of the processes to identify and assess material climate-related impacts, risks and opportunities", start: 144, end: 156, actualCount: 13, pillar: "Environment" },
  { label: "E1-2 – Policies related to climate change mitigation and adaptation", start: 157, end: 166, actualCount: 10, pillar: "Environment" },
  { label: "E1-3 – Actions and resources in relation to climate change policies", start: 167, end: 182, actualCount: 16, pillar: "Environment" },
  { label: "E1-4 – Targets related to climate change mitigation and adaptation", start: 183, end: 202, actualCount: 20, pillar: "Environment" },
  { label: "E1-5 – Energy consumption and mix", start: 203, end: 205, actualCount: 3, pillar: "Environment" },
  { label: "E1-6 – Gross Scopes 1, 2, 3 and Total GHG emissions", start: 206, end: 218, actualCount: 12, pillar: "Environment" },
  { label: "E1-7 – GHG removals and GHG mitigation projects financed through carbon credits", start: 219, end: 233, actualCount: 15, pillar: "Environment" },
  { label: "E1-8 – Internal carbon pricing", start: 234, end: 236, actualCount: 3, pillar: "Environment" },
  { label: "E1-9 – Anticipated financial effects from material physical and transition risks and potential climate-related opportunities", start: 237, end: 261, actualCount: 25, pillar: "Environment" },

  // POLLUTION
  { label: "ESRS 2 IRO-1 – Description of the processes to identify and assess material pollution-related impacts, risks and opportunities", start: 262, end: 264, actualCount: 3, pillar: "Environment" },
  { label: "E2-1 – Policies related to pollution", start: 265, end: 277, actualCount: 11, pillar: "Environment" },
  { label: "E2-2 – Actions and resources related to pollution", start: 278, end: 291, actualCount: 14, pillar: "Environment" },
  { label: "E2-3 – Targets related to pollution", start: 292, end: 314, actualCount: 23, pillar: "Environment" },
  { label: "E2-4 – Pollution of air, water and soil", start: 315, end: 326, actualCount: 12, pillar: "Environment" },
  { label: "E2-5 – Substances of concern and substances of very high concern", start: 327, end: 328, actualCount: 2, pillar: "Environment" },
  { label: "E2-6 – Anticipated financial effects from pollution-related impacts, risks and opportunities", start: 329, end: 336, actualCount: 8, pillar: "Environment" },

  // WATER & MARINE RESOURCES
  { label: "ESRS 2 IRO-1 – Description of the processes to identify and assess material water and marine resources-related impacts, risks and opportunities", start: 337, end: 339, actualCount: 3, pillar: "Environment" },
  { label: "E3-1 – Policies related to water and marine resources", start: 340, end: 358, actualCount: 17, pillar: "Environment" },
  { label: "E3-2 – Actions and resources related to water and marine resources", start: 359, end: 372, actualCount: 14, pillar: "Environment" },
  { label: "E3-3 – Targets related to water and marine resources", start: 373, end: 397, actualCount: 25, pillar: "Environment" },
  { label: "E3-4 – Water consumption", start: 398, end: 403, actualCount: 6, pillar: "Environment" },
  { label: "E3-5 – Anticipated financial effects from water and marine resources-related impacts, risks and opportunities", start: 404, end: 407, actualCount: 4, pillar: "Environment" },

  // E4 Biodiversity
  { label: "E4 ESRS 2 SBM-3 – Material impacts, risks and opportunities and their interaction with strategy and business model", start: 408, end: 413, actualCount: 4, pillar: "Environment" },
  { label: "ESRS 2 IRO-1 Description of processes to identify and assess material biodiversity and ecosystem-related impacts, risks and opportunities", start: 414, end: 427, actualCount: 12, pillar: "Environment" },
  { label: "E4-1 – Transition plan and consideration of biodiversity and ecosystems in strategy and business model", start: 428, end: 445, actualCount: 13, pillar: "Environment" },
  { label: "E4-2 – Policies related to biodiversity and ecosystems", start: 446, end: 473, actualCount: 28, pillar: "Environment" },
  { label: "E4-3 – Actions and resources related to biodiversity and ecosystems", start: 473, end: 496, actualCount: 23, pillar: "Environment" },
  { label: "E4-4 – Targets related to biodiversity and ecosystems", start: 497, end: 521, actualCount: 25, pillar: "Environment" },
  { label: "E4-5 – Impact metrics related to biodiversity and ecosystems change", start: 522, end: 545, actualCount: 23, pillar: "Environment" },
  { label: "E4-6 – Anticipated financial effects from biodiversity and ecosystem-related risks and opportunities", start: 546, end: 549, actualCount: 4, pillar: "Environment" },

  // E5 Circular Economy
  { label: "ESRS 2 IRO-1 – Description of the processes to identify and assess material resource use and circular economy-related impacts, risks and opportunities", start: 550, end: 557, actualCount: 8, pillar: "Environment" },
  { label: "E5-1 – Policies related to resource use and circular economy", start: 558, end: 570, actualCount: 12, pillar: "Environment" },
  { label: "E5-2 – Actions and resources related to resource use and circular economy", start: 571, end: 591, actualCount: 20, pillar: "Environment" },
  { label: "E5-3 – Targets related to resource use and circular economy", start: 592, end: 617, actualCount: 19, pillar: "Environment" },
  { label: "E5-4 – Resource inflows of Disclosure Requirement", start: 618, end: 624, actualCount: 7, pillar: "Environment" },
  { label: "E5-5 –Resource outflows", start: 625, end: 634, actualCount: 10, pillar: "Environment" },
  { label: "E5-6 – Anticipated financial effects from resource use and circular economy-related impacts, risks and opportunities", start: 635, end: 638, actualCount: 4, pillar: "Environment" },

  // SOCIAL S1 Own Workforce
  { label: "S1 ESRS 2 SBM-3 – Material impacts, risks and opportunities and their interaction with strategy and business model", start: 639, end: 650, actualCount: 11, pillar: "Social" },
  { label: "S1-1 – Policies related to own workforce", start: 651, end: 680, actualCount: 27, pillar: "Social" },
  { label: "S1-2 – Processes for engaging with own workers and workers' representatives about impacts", start: 681, end: 694, actualCount: 12, pillar: "Social" },
  { label: "S1-3 – Processes to remediate negative impacts and channels for own workers to raise concerns", start: 695, end: 703, actualCount: 9, pillar: "Social" },
  { label: "S1-4 – Taking action on material impacts on own workforce, and approaches to mitigating material risks and pursuing material opportunities related to own workforce, and effectiveness of those actions", start: 704, end: 734, actualCount: 30, pillar: "Social" },
  { label: "S1-5 – Targets related to managing material negative impacts, advancing positive impacts, and managing material risks and opportunities", start: 735, end: 755, actualCount: 19, pillar: "Social" },
  { label: "S1-6 – Characteristics of the undertaking's employees", start: 756, end: 765, actualCount: 8, pillar: "Social" },
  { label: "S1-7 – Characteristics of non-employee workers in the undertaking's own workforce", start: 766, end: 771, actualCount: 4, pillar: "Social" },
  { label: "S1-8 – Collective bargaining coverage and social dialogue", start: 772, end: 776, actualCount: 5, pillar: "Social" },
  { label: "S1-9 – Diversity metrics", start: 777, end: 779, actualCount: 3, pillar: "Social" },
  { label: "S1-10 – Adequate wages", start: 780, end: 782, actualCount: 3, pillar: "Social" },
  { label: "S1-11 – Social protection", start: 783, end: 788, actualCount: 2, pillar: "Social" },
  { label: "S1-12– Persons with disabilities", start: 789, end: 791, actualCount: 3, pillar: "Social" },
  { label: "S1-13 – Training and skills development metrics", start: 792, end: 792, actualCount: 1, pillar: "Social" },
  { label: "S1-14 – Health and safety metrics", start: 793, end: 801, actualCount: 10, extra: [1164], pillar: "Social" },
  { label: "S1-15 – Work-life balance metrics", start: 802, end: 802, actualCount: 1, pillar: "Social" },
  { label: "S1-16 – Compensation metrics (pay gap and total compensation)", start: 803, end: 804, actualCount: 2, pillar: "Social" },
  { label: "S1-17 – Incidents, complaints and severe human rights impacts", start: 806, end: 813, actualCount: 5, pillar: "Social" },

  // S2 Value Chain Workers
  { label: "ESRS 2 SBM-3 Material impacts, risks and opportunities and their interaction with strategy and business model", start: 814, end: 822, actualCount: 9, pillar: "Social" },
  { label: "S2-1 – Policies related to value chain workers", start: 823, end: 839, actualCount: 14, pillar: "Social" },
  { label: "S2-2 – Processes for engaging with value chain workers about impacts", start: 840, end: 847, actualCount: 6, pillar: "Social" },
  { label: "S2-3 – Processes to remediate negative impacts and channels for value chain workers to raise concerns", start: 848, end: 856, actualCount: 9, pillar: "Social" },
  { label: "S2-4 – Taking action on material impacts on value chain workers, and approaches to managing material risks and pursuing materials opportunities related to value chain workers, and effectiveness of those action", start: 857, end: 888, actualCount: 26, pillar: "Social" },
  { label: "S2-5 – Targets related to managing material negative impacts, advancing positive impacts, and managing material risks and opportunities", start: 889, end: 909, actualCount: 19, pillar: "Social" },

  // S3 Affected Communities
  { label: "ESRS 2 SBM-3 - Material impacts, risks and opportunities and their interaction with strategy and business model", start: 910, end: 917, actualCount: 8, pillar: "Social" },
  { label: "S3-1 – Policies related to affected communities", start: 918, end: 934, actualCount: 17, pillar: "Social" },
  { label: "S3-2 – Processes for engaging with affected communities about impacts", start: 935, end: 942, actualCount: 8, pillar: "Social" },
  { label: "S3-3 – Processes to remediate negative impacts and channels for affected communities to raise concerns", start: 943, end: 951, actualCount: 9, pillar: "Social" },
  { label: "S3-4 – Taking action on material impacts on affected communities, and approaches to managing material risks and pursuing material opportunities related to affected communities, and effectiveness of those actions", start: 952, end: 984, actualCount: 33, pillar: "Social" },
  { label: "S3-5 – Targets related to managing material negative impacts, advancing positive impacts, and managing material risks and opportunities", start: 985, end: 1005, actualCount: 21, pillar: "Social" },

  // S4 Consumers & End Users
  { label: "S4 ESRS 2 SBM-3 – Material impacts, risks and opportunities and their interaction with strategy and business model", start: 1006, end: 1013, actualCount: 8, pillar: "Social" },
  { label: "S4-1 – Policies related to consumers and end-users", start: 1014, end: 1029, actualCount: 13, pillar: "Social" },
  { label: "S4-2 – Processes for engaging with consumers and endusers about impacts", start: 1030, end: 1037, actualCount: 8, pillar: "Social" },
  { label: "S4-3 – Processes to remediate negative impacts and channels for consumers and end-users to raise concerns", start: 1038, end: 1047, actualCount: 10, pillar: "Social" },
  { label: "S4-4 – Taking action on material impacts on consumers and end- users, and approaches to managing material risks and pursuing material opportunities related to consumers and end-users, and effectiveness of those actions", start: 1048, end: 1079, actualCount: 29, pillar: "Social" },
  { label: "S4-5 – Targets related to managing material negative impacts, advancing positive impacts, and managing material risks and opportunities", start: 1080, end: 1100, actualCount: 18, pillar: "Social" },

  // GOVERNANCE G1
  { label: "ESRS 2 GOV-1 – The role of the administrative, supervisory and management bodies", start: 1101, end: 1101, actualCount: 1, pillar: "Governance" },
  { label: "G1-1– Corporate culture and bBusiness conduct policies and corporate culture", start: 1102, end: 1120, actualCount: 18, pillar: "Governance" },
  { label: "G1-2 – Management of relationships with suppliers", start: 1121, end: 1122, actualCount: 2, pillar: "Governance" },
  { label: "G1-3 – Prevention and detection of corruption and bribery", start: 1124, end: 1133, actualCount: 7, extra: [1165], pillar: "Governance" },
  { label: "G1-4 – Confirmed incidents of corruption or bribery", start: 1134, end: 1149, actualCount: 12, pillar: "Governance" },
  { label: "G1-5 – Political influence and lobbying activities", start: 1150, end: 1159, actualCount: 7, pillar: "Governance" },
  { label: "G1-6 – Payment practices", start: 1160, end: 1163, actualCount: 2, pillar: "Governance" },
];

// maps parameter name → list of DR IDs
const SECTION_DEFINITIONS = [
  {
    title: "General",
    drIds: ["general-disclosures"],
  },
  {
    title: "Environment",
    drIds: ["climate-change", "pollution", "water-marine-resources", "biodiversity-ecosystems", "resource-circular-economy"],
  },
  {
    title: "Social",
    drIds: ["own-workforce", "workers-value-chain", "affected-communities", "consumers-end-users"],
  },
  {
    title: "Governance",
    drIds: ["business-conduct"],
  },
];

const LEFT_SECTIONS = SECTION_DEFINITIONS.filter((sec) => sec.title === "General" || sec.title === "Environment");
const RIGHT_SECTIONS = SECTION_DEFINITIONS.filter((sec) => sec.title === "Social" || sec.title === "Governance");

const ReportDashboard = () => {
  // State Management
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);
  const [loading, setLoading] = React.useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [reportInfo, setReportInfo] = useState({ reportName: "", completionDate: "" });
  const [drStats, setDrStats] = useState([]);
  const [dataStatus, setDataStatus] = useState({ reported: 0, toDo: 0, notMaterial: 0 });
  const [esgBreakdown, setEsgBreakdown] = useState({
    General: { reported: 0, toDo: 0, notMaterial: 0 },
    Environment: { reported: 0, toDo: 0, notMaterial: 0 },
    Social: { reported: 0, toDo: 0, notMaterial: 0 },
    Governance: { reported: 0, toDo: 0, notMaterial: 0 },
  });
  const [dataCollection, setDataCollection] = useState({
    quantitativeAnswered: 0,
    qualitativeAnswered: 0,
  });
  const [quantitativeDataSources, setQuantitativeDataSources] = useState({
    automatedInputs: 0,
    manualInputs: 0,
    notAvailable: 0,
    totalCells: 0,
  });
  const [dataReadyTick, setDataReadyTick] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();
  const companyid = localStorage.getItem("companyid");

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  const extractQuestionNumber = (rawId) => {
    if (rawId == null) return null;
    const asString = String(rawId).trim();
    if (!asString) return null;
    const match = asString.match(/(\d+)/);
    if (!match) return null;
    const num = parseInt(match[1], 10);
    return Number.isNaN(num) ? null : num;
  };

  // const getPillar = (label) => {
  //   const lower = label.toLowerCase();
  //   if (lower.startsWith("bp") || lower.startsWith("gov-") || lower.startsWith("sbm") || lower.startsWith("iro")) {
  //     return "General";
  //   }
  //   if (lower.startsWith("e1") || lower.includes("pollution") || lower.includes("biodiversity") || lower.includes("water") || lower.includes("resource")) {
  //     return "Environment";
  //   }
  //   if (lower.startsWith("s1") || lower.startsWith("s2") || lower.startsWith("s3") || lower.startsWith("s4")) {
  //     return "Social";
  //   }
  //   if (lower.startsWith("g1") || lower.includes("business conduct")) {
  //     return "Governance";
  //   }
  //   return null;
  // };

  const renderHeader = () => {
    const loginType = localStorage.getItem("loginType");
    if (loginType === "AAD") {
      return <AADHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />;
    }
    return <LoginHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />;
  };

  // Window Resize Handler
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsSidebarOpen(false);
      } else {
        setIsSidebarOpen(true);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Load Report Information
  useEffect(() => {
    const statusData = localStorage.getItem("reportStatusData");
    if (statusData) {
      const parsed = JSON.parse(statusData);
      const formattedDate = new Date(parsed.completionDate).toLocaleDateString();
      setReportInfo({
        reportName: parsed.reportName || "Untitled Report",
        completionDate: formattedDate || "N/A",
      });
    }

    // Calculate DR Stats
    const generateReport = JSON.parse(localStorage.getItem("step4") || "[]");
    const answered = generateReport.filter((q) => {
      const answerText = q?.answer == null ? "" : String(q.answer).trim();
      return answerText !== "";
    });

    const stats = DR_RANGES.map(({ id, label, start, end, actualCount }) => {
      const answeredCount = answered.filter(({ id: qid }) => {
        const num = extractQuestionNumber(qid);
        return num != null && num >= start && num <= end;
      }).length;
      return { id, label, total: actualCount, answered: answeredCount };
    });

    setDrStats(stats);
  }, [dataReadyTick]);

  // Load report payload when opened from CSRD status icon on Standards table.
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const fileNameFromQuery = searchParams.get("file");
    const fileName = fileNameFromQuery || localStorage.getItem("statusReportFile");
    if (!fileName) return;

    const fetchStatusReportData = async () => {
      setLoading(true);
      try {
        const tokenRes = await fetch(`${process.env.REACT_APP_BASE_API_URL}GetSASToken`, {
          method: "GET",
          headers: { "Content-type": "application/json; charset=UTF-8" },
        });
        if (tokenRes.status !== 200) {
          setLoading(false);
          return;
        }

        const tokenJson = await tokenRes.json();
        const blobServiceClient = new BlobServiceClient(`${tokenJson.data}`);
        const containerClient = blobServiceClient.getContainerClient("ecoprismfilecontainer");
        const normalizedFileName = String(fileName).includes("/") ? String(fileName).split("/").pop() : String(fileName);
        let downloadResponse;
        try {
          const blobClient = containerClient.getBlobClient(`${companyid}/${normalizedFileName}`);
          downloadResponse = await blobClient.download(0);
        } catch (_) {
          // Some records can store relative full path already; fallback to raw file key.
          const blobClientFallback = containerClient.getBlobClient(String(fileName));
          downloadResponse = await blobClientFallback.download(0);
        }
        const blob = await downloadResponse.blobBody;
        const downloadedText = await blob.text();
        const jsonData = JSON.parse(downloadedText);

        const toArray = (val) => {
          if (Array.isArray(val)) return val;
          if (typeof val === "string") {
            try {
              const parsed = JSON.parse(val);
              return Array.isArray(parsed) ? parsed : [];
            } catch (_) {
              return [];
            }
          }
          return [];
        };
        const toObject = (val) => {
          if (val && typeof val === "object" && !Array.isArray(val)) return val;
          if (typeof val === "string") {
            try {
              const parsed = JSON.parse(val);
              return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
            } catch (_) {
              return {};
            }
          }
          return {};
        };

        // Hydrate the same keys used by dashboard analytics so this page never opens empty.
        localStorage.setItem(
          "step1",
          JSON.stringify({
            name: jsonData?.name || "",
            timeline: jsonData?.timeline || "",
            legalEntity: jsonData?.legalEntity || "",
            startDate: jsonData?.startDate || "",
            endDate: jsonData?.endDate || "",
          }),
        );
        localStorage.setItem("step2", JSON.stringify(toArray(jsonData?.materiality)));
        localStorage.setItem("step3", JSON.stringify(toArray(jsonData?.reportingModule)));
        localStorage.setItem("step4", JSON.stringify(toArray(jsonData?.generateReport)));
        localStorage.setItem("answers", JSON.stringify(toObject(jsonData?.answers)));
        localStorage.setItem("answers1", JSON.stringify(toObject(jsonData?.answers1)));
        localStorage.setItem("tableAnswers", JSON.stringify(toObject(jsonData?.tableAnswers)));
        localStorage.setItem("selectedOptions", JSON.stringify(toObject(jsonData?.selectedOptions)));
        localStorage.setItem("mdrp", JSON.stringify(toObject(jsonData?.mdrp)));

        localStorage.setItem(
          "reportStatusData",
          JSON.stringify({
            reportName: jsonData?.name || "Untitled Report",
            completionDate: jsonData?.timeline || "",
          }),
        );

        const formattedDate = jsonData?.timeline ? new Date(jsonData.timeline).toLocaleDateString() : "N/A";
        setReportInfo({
          reportName: jsonData?.name || "Untitled Report",
          completionDate: formattedDate,
        });
        setDataReadyTick((prev) => prev + 1);
      } catch (error) {
        console.error("Error loading report for dashboard:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStatusReportData();
  }, [location.search, companyid]);

  useEffect(() => {
    // load selections & answers
    const selectedSet = new Set(JSON.parse(localStorage.getItem("step3") || "[]").map((i) => i.label));
    const answeredSet = new Set(
      JSON.parse(localStorage.getItem("step4") || "[]")
        .filter((q) => {
          const answerText = q?.answer == null ? "" : String(q.answer).trim();
          return answerText !== "" && q?.id != null;
        })
        .map((q) => extractQuestionNumber(q.id))
        .filter((num) => num != null)
        .map((num) => String(num)),
    );

    // init accumulators
    let overall = { reported: 0, toDo: 0, notMaterial: 0 };
    const perPillar = {
      General: { reported: 0, toDo: 0, notMaterial: 0 },
      Environment: { reported: 0, toDo: 0, notMaterial: 0 },
      Social: { reported: 0, toDo: 0, notMaterial: 0 },
      Governance: { reported: 0, toDo: 0, notMaterial: 0 },
    };

    DR_SUBTOPICS.forEach(({ label, start, end, actualCount, extra = [], pillar }) => {
      // **1. Count how many were answered**
      let answeredCount = 0;

      // Count answered within the start–end range
      answeredSet.forEach((qid) => {
        const num = parseInt(qid, 10);
        if (num >= start && num <= end) answeredCount++;
      });
      // Count extras answered
      extra.forEach((e) => {
        if (answeredSet.has(String(e))) answeredCount++;
      });

      // **2. Distribute into categories**
      const isSel = selectedSet.has(label);

      let rep = 0,
        todo = 0,
        nm = 0;
      if (isSel) {
        rep = answeredCount;
        todo = actualCount - answeredCount;
        nm = 0;
      } else {
        rep = 0;
        todo = 0;
        nm = actualCount;
      }

      // **3. Roll up**
      overall.reported += rep;
      overall.toDo += todo;
      overall.notMaterial += nm;

      perPillar[pillar].reported += rep;
      perPillar[pillar].toDo += todo;
      perPillar[pillar].notMaterial += nm;
    });

    setDataStatus(overall);
    setEsgBreakdown(perPillar);
  }, [dataReadyTick]);

  const TABLE_IDS = new Set(["34", "35", "60", "185", "203", "204", "205", "208", "209", "221", "222", "234", "238", "398", "400", "619", "620", "621", "626", "628", "629", "632", "756", "766", "772", "790", "792", "802", "803", "1133", "1164", "1165"]);

  useEffect(() => {
    const step4 = JSON.parse(localStorage.getItem("step4") || "[]");

    let qualitativeAnswered = 0;
    let totalAnswered = 0;

    step4.forEach((q) => {
      if (!q.answer?.trim()) return; // skip unanswered
      if (!q.id || typeof q.id !== "string") return; // skip if id is missing or not a string
      const id = q.id.replace("q-", "");
      totalAnswered++;
      if (TABLE_IDS.has(id)) qualitativeAnswered++;
    });

    const totalTables = TABLE_IDS.size; // 32
    const totalQuestions = actualTotalPoints; // 1067
    const totalQuantitative = totalQuestions - totalTables;

    const quantitativeAnswered = totalAnswered - qualitativeAnswered;

    setDataCollection({ quantitativeAnswered, qualitativeAnswered });
  }, [dataReadyTick]);

  // Calculate quantitative data sources from table data
  useEffect(() => {
    const calculateQuantitativeDataSources = () => {
      // Define the total possible cells for all 32 tables based on TABLE_IDS
      // This represents the complete table structure across all DRs
      const tableStructures = {
        // Energy consumption tables (E1-5-1, E1-5-2, etc.)
        203: 17 * 4, // e1_5_1: 17 rows × 4 columns (comp3, comp2, comparative, yearN)
        204: 2 * 5, // e1_5_2: 2 rows × 5 columns (comp3, comp2, comparative, yearN, percentage)
        205: 3 * 1, // e1_5_3: 3 rows × 1 column (netData)

        // GHG emissions tables (E1-6-1)
        208: 7 * 10, // e1_6_1: 7 emission types × 10 columns (baseYear, comp3, comp2, etc.)
        209: 7 * 10, // Similar structure

        // Social tables (S1-6, S1-7, S1-8, etc.)
        756: 6 * 8, // s1_6: Employee characteristics - 6 categories × 8 demographic breakdowns
        766: 2 * 5, // s1_7: Non-employee workers - 2 types × 5 demographics
        772: 5 * 3, // s1_8: Collective bargaining - 5 age ranges × 3 regions
        790: 4 * 5, // s1_12: Persons with disabilities
        792: 1 * 8, // s1_13: Training metrics
        802: 1 * 8, // s1_15: Work-life balance
        803: 2 * 8, // s1_16: Compensation metrics

        // Other tables with estimated cell counts based on typical structure
        34: 12, // General disclosures table
        35: 15, // General disclosures table
        60: 8, // Governance table
        185: 20, // Climate targets table
        221: 18, // Climate metrics table
        222: 16, // Climate metrics table
        234: 12, // Climate financial effects
        238: 25, // Climate financial effects detailed
        398: 10, // Water consumption
        400: 12, // Water metrics
        619: 8, // Resource inflows
        620: 10, // Resource inflows detailed
        621: 8, // Resource outflows
        626: 12, // Resource outflows detailed
        628: 15, // Resource metrics
        629: 10, // Resource metrics
        632: 8, // Resource financial effects
        1133: 6, // Governance table
        1164: 10, // Additional health/safety metrics
        1165: 8, // Additional governance metrics
      };

      // Calculate total possible cells across all tables
      const totalPossibleCells = Object.values(tableStructures).reduce((sum, cells) => sum + cells, 0);

      const tableAnswers = JSON.parse(localStorage.getItem("tableAnswers") || "{}");

      let automatedInputs = 0;
      let manualInputs = 0;
      let notAvailable = 0;

      // Define patterns for automated fields (filled from backend APIs)
      const automatedPatterns = [
        "-yearN", // Current year data from APIs
        "-comparative", // Comparative year data from APIs
        "-baseYear", // Base year data from APIs
        "-comp2", // Comparative data 2
        "-comp3", // Comparative data 3
        "_auto_", // Explicitly marked as automated
        "nuclear-", // Nuclear energy data (typically from energy APIs)
        "totalEnergy-", // Total energy consumption (calculated/automated)
      ];

      // Define patterns for manual input fields
      const manualPatterns = [
        "manual-", // Explicitly marked as manual
        "_input_", // Input fields
        "custom-", // Custom input fields
        "user-", // User input fields
      ];

      // Count filled cells and categorize them
      let filledCells = 0;
      Object.keys(tableAnswers).forEach((key) => {
        const value = tableAnswers[key];

        // Convert value to string and check if it's not empty
        const stringValue = value != null ? String(value) : "";

        if (stringValue && stringValue.trim() !== "") {
          filledCells++;

          // Check if this is an automated field (from backend API)
          const isAutomated = automatedPatterns.some((pattern) => key.includes(pattern));
          const isManual = manualPatterns.some((pattern) => key.includes(pattern));

          if (isAutomated && !isManual) {
            automatedInputs++;
          } else {
            // Default to manual if not clearly automated
            manualInputs++;
          }
        }
      });

      // Calculate not available cells (total possible - filled)
      notAvailable = totalPossibleCells - filledCells;

      setQuantitativeDataSources({
        automatedInputs,
        manualInputs,
        notAvailable,
        totalCells: totalPossibleCells, // Use total possible cells as the center number
      });
    };

    calculateQuantitativeDataSources();
  }, [dataReadyTick]);

  const actualTotalPoints = DR_SUBTOPICS.reduce((sum, topic) => sum + topic.actualCount, 0);

  // Example Highcharts options for different charts
  const dataPointsChartOptions = {
    chart: {
      type: "pie",
      height: 200,
      events: {
        render: function () {
          // Add a central label (1582)
          const chart = this;
          const centerX = chart.plotWidth / 1.8 + chart.plotLeft;
          const centerY = chart.plotHeight / 2 + chart.plotTop;

          // Remove existing custom labels to prevent overlap
          if (chart.customLabel) chart.customLabel.destroy();

          chart.customLabel = chart.renderer
            .text(actualTotalPoints.toString(), centerX, centerY)
            .css({
              color: "#000", // Center text color
              fontSize: "16px", // Font size
              fontWeight: "500",
              textAlign: "right",
            })
            .attr({
              align: "center",
            })
            .add()
            .toFront();

          // Center the text properly
          chart.customLabel.attr({
            x: centerX - chart.customLabel.getBBox().width / 2,
            y: centerY + chart.customLabel.getBBox().height / 4,
          });
        },
      },
    },
    title: {
      text: "Data Points Status",
      align: "left",
      margin: 30, // Increase margin to create space between the title and legend
    },
    credits: {
      enabled: false, // Hide the Highcharts.com label
    },
    legend: {
      align: "left", // Align legend to the left
      verticalAlign: "top", // Align legends vertically at the top
      layout: "vertical", // Stack legends vertically
      itemStyle: {
        fontSize: "12px", // Adjust the font size for better readability
        fontWeight: "normal",
      },
      labelFormat: "{name}", // Display the series name in the legend
      y: 40, // Add vertical offset to move the legend down
    },
    plotOptions: {
      pie: {
        size: "100%",
        innerSize: "70%", // Convert pie chart to donut chart
        dataLabels: {
          enabled: true, // Enable labels on the chart
          format: "{point.percentage:.0f}%", // Show values only
        },
        showInLegend: true, // Show data points in the legend
      },
    },
    tooltip: {
      pointFormat: "{point.name}: <b>{point.percentage:.0f}%</b>", // Tooltip with percentages
    },
    series: [
      {
        name: "Status",
        data: [
          { name: "Reported", y: dataStatus.reported, color: "#407775" },
          { name: "Not Material", y: dataStatus.notMaterial, color: "#fab430" },
          { name: "To Do", y: dataStatus.toDo, color: "#B4C9C8" },
        ],
      },
    ],
  };

  const totalTables = 32;
  const totalQuestions = actualTotalPoints; // 1067
  const totalQuantitative = totalQuestions - totalTables;

  const qualitativePct = (dataCollection.qualitativeAnswered / TABLE_IDS.size) * 100;
  const quantitativePct = (dataCollection.quantitativeAnswered / (actualTotalPoints - TABLE_IDS.size)) * 100;

  const dataCollectionChartOptions = {
    chart: {
      type: "bar", // Change to "bar" for horizontal bars
      height: 150,
    },
    title: {
      text: "Data Collection",
      align: "left", // Align the title to the left for consistency
    },
    xAxis: {
      categories: ["Quantitative", "Qualitative"],
      lineWidth: 0,
      title: {
        text: null, // Remove x-axis title
      },
    },
    yAxis: {
      title: {
        text: null, // Remove y-axis title
      },
      labels: {
        formatter: function () {
          return this.value + "%"; // Add % to y-axis labels
        },
      },
      max: 50, // Set the maximum value to 50%
      tickInterval: 10,
    },
    credits: {
      enabled: false, // Hide the Highcharts.com label
    },
    legend: {
      enabled: false, // Remove legend
    },
    plotOptions: {
      bar: {
        pointPadding: 0.1, // Adjust padding between bars
        groupPadding: 0.5, // Adjust padding between groups
        borderWidth: 0, // Remove border for a cleaner look
        pointWidth: 20,
      },
    },
    series: [
      {
        name: "Data",
        data: [
          { y: quantitativePct, name: `${dataCollection.quantitativeAnswered}/${totalQuantitative}`, color: "#ADA9A9" },
          { y: qualitativePct, name: `${dataCollection.qualitativeAnswered}/${TABLE_IDS.size}`, color: "#FAB430" },
        ],
      },
    ],
  };

  const quantitativeDataSourcesOptions = {
    chart: {
      type: "pie",
      height: 200,
      events: {
        render: function () {
          // Add a central label (total cells count)
          const chart = this;
          const centerX = chart.plotWidth / 1.8 + chart.plotLeft;
          const centerY = chart.plotHeight / 2 + chart.plotTop;

          // Remove existing custom labels to prevent overlap
          if (chart.customLabel) chart.customLabel.destroy();

          chart.customLabel = chart.renderer
            .text(
              quantitativeDataSources.totalCells.toString(), // Dynamic total cells count
              centerX,
              centerY,
            )
            .css({
              color: "#000", // Center text color
              fontSize: "16px", // Font size
              fontWeight: "500",
              textAlign: "right",
            })
            .attr({
              align: "center",
            })
            .add()
            .toFront();

          // Center the text properly
          chart.customLabel.attr({
            x: centerX - chart.customLabel.getBBox().width / 2,
            y: centerY + chart.customLabel.getBBox().height / 4,
          });
        },
      },
    },
    title: {
      text: "Quantitative Data Sources",
      align: "left",
      margin: 30, // Increase margin to create space between the title and legend
    },
    credits: {
      enabled: false, // Hide the Highcharts.com label
    },
    legend: {
      align: "left", // Align legend to the left
      verticalAlign: "top", // Align legends vertically at the top
      layout: "vertical", // Stack legends vertically
      itemStyle: {
        fontSize: "12px", // Adjust the font size for better readability
        fontWeight: "normal",
      },
      labelFormat: "{name}", // Display the series name in the legend
      y: 40, // Add vertical offset to move the legend down
    },
    plotOptions: {
      pie: {
        size: "100%",
        innerSize: "70%", // Convert pie chart to donut chart
        dataLabels: {
          enabled: true, // Enable labels on the chart
          format: "{point.percentage:.0f}%", // Show values only
        },
        showInLegend: true, // Show data points in the legend
      },
    },
    tooltip: {
      pointFormat: "{point.name}: <b>{point.percentage:.0f}%</b>", // Tooltip with percentages
    },
    series: [
      {
        name: "Status",
        data: [
          { name: "Automated inputs", y: quantitativeDataSources.automatedInputs, color: "#407775" },
          { name: "Manual Inputs", y: quantitativeDataSources.manualInputs, color: "#fab430" },
          { name: "Not available", y: quantitativeDataSources.notAvailable, color: "#B4C9C8" },
        ],
      },
    ],
  };

  const esgStatusChartOptions = {
    chart: {
      type: "bar",
    },
    credits: {
      enabled: false, // Hide the Highcharts.com label
    },
    title: {
      text: "Data Status for ESG",
      align: "left",
    },
    xAxis: {
      categories: ["To Do", "Not Material", "Reported"],
      lineWidth: 0, // Remove the y-axis line
    },
    yAxis: {
      title: {
        text: null, // Remove y-axis title
      },
      lineWidth: 0, // Remove the y-axis line
      labels: {
        enabled: true, // Enable/disable labels as per your needs
      },
    },
    plotOptions: {
      bar: {
        pointPadding: 0, // Remove space between bars within a category
        borderWidth: 0, // Remove bar borders for a cleaner look
        borderRadius: 0,
      },
    },
    series: [
      {
        name: "Governance",
        data: [esgBreakdown.Governance.toDo, esgBreakdown.Governance.notMaterial, esgBreakdown.Governance.reported],
        color: "#B4C9C8",
      },
      {
        name: "Social",
        data: [esgBreakdown.Social.toDo, esgBreakdown.Social.notMaterial, esgBreakdown.Social.reported],
        color: "#fab430",
      },
      {
        name: "Environment",
        data: [esgBreakdown.Environment.toDo, esgBreakdown.Environment.notMaterial, esgBreakdown.Environment.reported],
        color: "#407775",
      },

      {
        name: "General",
        data: [esgBreakdown.General.toDo, esgBreakdown.General.notMaterial, esgBreakdown.General.reported],
        color: "#A9A9A9",
      },
    ],
  };

  return (
    <div id="wrapper">
      <div>
        {loading && (
          <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={true}>
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
      </div>
      {renderHeader()}
      <SideNavigation isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <div id="page-content-wrapper" className={isSidebarOpen ? "page-content-wrapper-pd-0" : "page-wrapper"}>
        <div class="container formpd">
          <div class="flex justify-content-between">
            <div class="steps__containt-hader">
              <h3 class="title">{reportInfo.reportName}</h3>
              <div role="presentation">
                <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
                  <Link underline="hover" color="inherit" href="/standards">
                    Reports
                  </Link>
                  <Link underline="hover" color="inherit" href="/standards">
                    Standards & Frameworks
                  </Link>
                  <Typography color="text.primary">{reportInfo.reportName}</Typography>
                </Breadcrumbs>
              </div>
            </div>
            <div className="stepper-box">
              <Stepper activeStep={3} alternativeLabel className="csrd-stepper">
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel className="step-name">{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </div>
          </div>
          <div className="button-box d-flex justify-content-end mb-4">
            <Button
              variant="outlined"
              className="module-btn"
              size="medium"
              sx={{ marginRight: "15px" }}
              onClick={() => navigate(-1)} // Navigate back to the previous page
            >
              <WestIcon fontSize="small" sx={{ marginRight: "4px" }} />
              Back
            </Button>
          </div>
          <div className="report-dashboard pt-6">
            <div className="row mb-4">
              <div className="col-md-6 mb-3">
                {/* First Card: EU CSRD */}
                <div className="card mb-3 title-esrs">
                  <div className="card-body">
                    <h6 className="card-subtitle text-muted mb-2">EU CSRD</h6>
                    <h4 className="card-title">{reportInfo.reportName}</h4>
                  </div>
                </div>

                {/* Second Card: Completion By */}
                <div className="card  title-esrs">
                  <div className="card-body">
                    <h6 className="card-subtitle text-muted mb-2">Completion By</h6>
                    <h4 className="card-title">{reportInfo.completionDate}</h4>
                  </div>
                </div>
              </div>

              {/* Data Collection Chart */}
              <div className="col-md-6 mb-3">
                <div className="card shadow">
                  <div className="card-body">
                    <HighchartsReact highcharts={Highcharts} options={dataPointsChartOptions} />
                  </div>
                </div>
              </div>

              <div className="col-md-6">
                <div className="card shadow">
                  <div className="card-body">
                    <HighchartsReact highcharts={Highcharts} options={esgStatusChartOptions} />
                  </div>
                </div>
              </div>

              <div className="col-md-6">
                <div className="card shadow mb-3">
                  <div className="card-body">
                    <HighchartsReact highcharts={Highcharts} options={dataCollectionChartOptions} />
                  </div>
                </div>
                <div className="card shadow">
                  <div className="card-body">
                    <HighchartsReact highcharts={Highcharts} options={quantitativeDataSourcesOptions} />
                  </div>
                </div>
              </div>
            </div>

            <div className="row main-factors">
              {/* Left side: General + Environment */}
              <div className="col-md-6">
                {LEFT_SECTIONS.map(({ title, drIds }) => (
                  <ul className="list-group" key={title}>
                    <li className="list-group-item mb-4">
                      <div className="factor-title">{title}</div>
                      {drIds.map((id) => {
                        const { label, answered, total } = drStats.find((d) => d.id === id) || {};
                        return (
                          <div className="factor-item" key={id}>
                            <div className="d-flex justify-content-between align-items-center" style={{ gap: "20px" }}>
                              <label className="link-label">{label}</label>
                              <div className="d-flex align-items-center">
                                <span className="me-4 question-count">
                                  {answered}/{total}
                                </span>
                                <div className="progress" style={{ width: "100px" }}>
                                  <div
                                    className="progress-bar"
                                    style={{
                                      width: total > 0 ? ((answered / total) * 100).toFixed(1) + "%" : "0%",
                                      background: "linear-gradient(145deg, #407775 25%, #407775 75%, transparent 75%)",
                                      height: "100%",
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </li>
                  </ul>
                ))}
              </div>

              {/* Right side: Social + Governance */}
              <div className="col-md-6">
                {RIGHT_SECTIONS.map(({ title, drIds }) => (
                  <ul className="list-group" key={title}>
                    <li className="list-group-item mb-4">
                      <div className="factor-title">{title}</div>
                      {drIds.map((id) => {
                        const { label, answered, total } = drStats.find((d) => d.id === id) || {};
                        return (
                          <div className="factor-item" key={id}>
                            <div className="d-flex justify-content-between align-items-center" style={{ gap: "20px" }}>
                              <label className="link-label">{label}</label>
                              <div className="d-flex align-items-center">
                                <span className="me-4 question-count">
                                  {answered}/{total}
                                </span>
                                <div className="progress" style={{ width: "100px" }}>
                                  <div
                                    className="progress-bar"
                                    style={{
                                      width: total > 0 ? ((answered / total) * 100).toFixed(1) + "%" : "0%",
                                      background: "linear-gradient(145deg, #407775 25%, #407775 75%, transparent 75%)",
                                      height: "100%",
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </li>
                  </ul>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportDashboard;
